import React, { useEffect, useRef, useState } from "react";
import configProvider from "config/configProvider";
import { Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import "mapbox-gl/dist/mapbox-gl.css";
import { Map, MapRef, Marker, MarkerDragEvent, Popup, Projection } from "react-map-gl";
import MDBox from "components/MDBox";
import Pin from "./Pin";
import { useMapWebglRecovery } from "map/useMapWebglRecovery";

export interface NearbyMarker {
  /** Stable key for the marker. */
  id: string | number;
  latitude: number;
  longitude: number;
  label?: string;
  /** Secondary line in the popup -- an address, a hub list, whatever the caller has. */
  detail?: string;
  /** Rendered muted. Used for inactive rows so they read as context rather than current. */
  isMuted?: boolean;
}

/**
 * One draggable pin plus read-only context markers around it.
 *
 * A sibling of SingleMarkerMap rather than a change to it. SingleMarkerMap's pin is static and
 * that is correct everywhere it is used -- the Point detail screen shows where a point is, it
 * does not let anyone move it. Placing a new point is the opposite job, so it gets its own
 * component instead of a `draggable` flag threaded through a screen that should never set it.
 *
 * The drag is the point of the screen. A geocoded address lands on a rooftop or a street
 * centreline, which for a rail yard is routinely the wrong side of a fence from the gate a crew
 * is actually driven to. The user drags to the real spot and the coordinate fields follow.
 */

/** The pin being placed. Deliberately unlike Pin (the red marker used for existing points). */
const DraggablePin = ({ isDragging = false }) => (
  <svg
    height={isDragging ? 40 : 34}
    viewBox="0 0 24 24"
    style={{
      cursor: "grab",
      fill: "#1A73E8",
      stroke: "#fff",
      strokeWidth: 1.2,
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,.45))",
    }}
  >
    <path
      d="M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
      c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
      C20.1,15.8,20.2,15.8,20.2,15.7z"
    />
    <circle cx="12" cy="10" r="3.2" fill="#fff" stroke="none" />
  </svg>
);

/** Close enough to be the same coordinate. Roughly a centimetre -- far below anything a drag or
 *  a geocode produces, so it only ever matches a value this component itself emitted. */
const SAME_COORDINATE_EPSILON = 1e-7;

/** Zoom used when the pin is placed from outside -- close enough to see a gate on satellite. */
const PLACEMENT_ZOOM = 16;

const isPlottable = (marker: NearbyMarker) =>
  typeof marker?.latitude === "number" &&
  typeof marker?.longitude === "number" &&
  !(marker.latitude === 0 && marker.longitude === 0);

const DraggableMarkerMap = ({
  latitude,
  longitude,
  onDragEnd,
  nearby = [],
  height = "47vh",
}: {
  latitude: number;
  longitude: number;
  onDragEnd: (latitude: number, longitude: number) => void;
  nearby?: NearbyMarker[];
  height?: string;
}) => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps } = useMapWebglRecovery(mapRef);
  const [mapCamera, setMapCamera] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 3.5,
  });
  const [toggleSatellite, setToggleSatellite] = useState(false);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/standard");
  const [projection] = useState<Projection>({ name: "mercator" });
  const [isDragging, setIsDragging] = useState(false);
  const [openMarkerId, setOpenMarkerId] = useState<string | number | null>(null);

  // The coordinate this map last emitted from a drag. Used to tell "the parent moved the pin"
  // (a geocode result, a typed coordinate -- recentre on it) from "the parent is echoing back
  // the drag that just happened" (leave the camera alone; yanking it under a hand that has just
  // let go of the pin is disorienting).
  const lastDraggedRef = useRef<{ latitude: number; longitude: number } | null>(null);

  const handleLayerToggle = (e) => {
    if (mapRef.current?.getStyle()?.name === "Mapbox Satellite Streets") {
      setMapStyle("mapbox://styles/mapbox/streets-v11");
    } else {
      setMapStyle("mapbox://styles/mapbox/satellite-streets-v12");
    }

    setToggleSatellite(e.target.checked);
  };

  const hasPin = Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!hasPin) return;

    const dragged = lastDraggedRef.current;
    const echoesOwnDrag =
      dragged !== null &&
      Math.abs(dragged.latitude - latitude) < SAME_COORDINATE_EPSILON &&
      Math.abs(dragged.longitude - longitude) < SAME_COORDINATE_EPSILON;

    if (echoesOwnDrag) return;

    setMapCamera((camera) => ({
      latitude,
      longitude,
      zoom: Math.max(camera.zoom, PLACEMENT_ZOOM),
    }));
  }, [latitude, longitude, hasPin]);

  const handleDragEnd = (event: MarkerDragEvent) => {
    setIsDragging(false);
    const { lat, lng } = event.lngLat;
    lastDraggedRef.current = { latitude: lat, longitude: lng };
    onDragEnd(lat, lng);
  };

  const plottableNearby = (nearby ?? []).filter(isPlottable);
  const openMarker = plottableNearby.find((m) => m.id === openMarkerId);

  return (
    <div>
      <MDBox>
        <FormGroup sx={{ alignItems: "end" }}>
          <FormControlLabel
            label="Satellite View"
            control={
              <Checkbox id="satellite-draggable" checked={toggleSatellite} onChange={handleLayerToggle} />
            }
          />
        </FormGroup>
      </MDBox>

      <Map
        key={mapKey}
        reuseMaps={reuseMaps}
        {...mapCamera}
        onMove={(e) => setMapCamera(e.viewState)}
        // styleDiffing off: react-map-gl defaults to diffing one style into the next, and
        // the streets -> satellite-streets hop cannot be diffed -- mapbox logs "Unable to
        // perform style diff: Unimplemented: setSprite. Rebuilding the style from scratch."
        // and the rebuild leaves the map with a style but no live sources, so it stops
        // requesting tiles and paints the style's background colour. That background is
        // near-black, which is the black satellite map users report. Reload cleanly instead.
        styleDiffing={false}
        mapStyle={mapStyle}
        style={{ width: "100%", height: height }}
        mapboxAccessToken={configProvider.MapboxApiKey}
        ref={mapRef}
        projection={projection}
      >
        {/* Existing points first, so the draggable pin paints over them rather than under. */}
        {plottableNearby.map((marker) => (
          <Marker
            key={marker.id}
            latitude={marker.latitude}
            longitude={marker.longitude}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setOpenMarkerId(marker.id);
            }}
          >
            <div style={{ opacity: marker.isMuted ? 0.4 : 0.85 }}>
              <Pin />
            </div>
          </Marker>
        ))}

        {openMarker && (
          <Popup
            latitude={openMarker.latitude}
            longitude={openMarker.longitude}
            anchor="top"
            onClose={() => setOpenMarkerId(null)}
            closeOnClick={false}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {openMarker.label}
            </Typography>
            {openMarker.detail && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {openMarker.detail}
              </Typography>
            )}
          </Popup>
        )}

        {hasPin && (
          <Marker
            latitude={latitude}
            longitude={longitude}
            anchor="bottom"
            draggable
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          >
            <DraggablePin isDragging={isDragging} />
          </Marker>
        )}
      </Map>
    </div>
  );
};

export default DraggableMarkerMap;
